import AddTaskIcon from "@mui/icons-material/AddTask";
import {
  Box,
  Button,
  IconButton,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import {useEffect, useState} from "react";
import MWCaption from "../../components/MWCaption";
import SubTitle from "../../components/SubTitle";
import {FormContents, FormSection} from "../../styledComponents/StyledCreateForm";

import * as statisticsCategoryApi from "../../api/statisticsApi/statisticsCategoryApi";
import * as statisticsItem1Api from "../../api/statisticsApi/statisticsItem1Api";
import * as statisticsItem2Api from "../../api/statisticsApi/statisticsItem2Api";
import * as Params from "../../models/clientModels/interfaces/apiParams";

import DeleteIcon from "@mui/icons-material/Delete";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import {useQueryClient} from "react-query";
import {Link, useNavigate} from "react-router-dom";
import {useRecoilValue, useSetRecoilState} from "recoil";
import MWProgress from "../../components/MWProgress";
import {useGetQuery, useRemoveMutation} from "../../hooks/useQueryHooks";
import {ResponseList, ResponseRemove} from "../../models/clientModels/interfaces/Response";
import {
  initialStatisticsItem2ListParams,
  WOStatistics2,
} from "../../models/serverModels/workOrder/WOStatistics2";
import {
  initialStatisticsCategoryListParams,
  WOStatisticsCategory,
} from "../../models/serverModels/workOrder/WOStatisticsCategory";
import {
  initializeStatisticsItem,
  WOStatisticsItem,
} from "../../models/serverModels/workOrder/WOStatisticsItem";
import {alertStateAtom} from "../../Recoil/Atom/AlertAtom";
import {tokenSelector} from "../../Recoil/Atom/authState";

const StyledTableHead = styled(TableHead)(({theme}) => ({
  backgroundColor: theme.palette.gray.dark,
}));

const ListContainer = styled(Box)(({theme}) => ({
  minWidth: "1700px",
  "& td, & tr, & th": {
    border: `1px solid ${theme.palette.warning.main}`,
    padding: "8px",
  },
}));

const StatisticsList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const theme = useTheme();
  const tokenState = useRecoilValue(tokenSelector);

  const setAlertState = useSetRecoilState(alertStateAtom);

  const [statisticsItem, setStatisticsItem] = useState<WOStatisticsItem[][]>([]);

  // note List
  // note statisticsCategoryList
  const {data: categoryList, isLoading: isCategoryLoading} = useGetQuery<
    ResponseList<WOStatisticsCategory>
  >({
    key: ["statisticsCategoryList", {...initialStatisticsCategoryListParams, token: tokenState}],
    api: () =>
      statisticsCategoryApi.getList({...initialStatisticsCategoryListParams, token: tokenState}),
  });

  // note statistics2List
  const {data: statistics2List, isLoading: isStatistics2Loading} = useGetQuery<
    ResponseList<WOStatistics2>
  >({
    key: ["statistics2List", {...initialStatisticsItem2ListParams, token: tokenState}],
    api: () => statisticsItem2Api.getList({...initialStatisticsItem2ListParams, token: tokenState}),
  });

  // note 삭제 쿼리
  const {mutate: categoryRemoveMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: statisticsCategoryApi.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("statisticsCategoryList");
      },
    },
  });
  const {mutate: Item1RemoveMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: statisticsItem1Api.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("statisticsCategoryList");
      },
    },
  });

  const {mutate: Item2RemoveMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: statisticsItem2Api.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("statistics2List");
      },
    },
  });

  // note reverse func
  const itemsReverseHandler = (data: ResponseList<WOStatisticsCategory>) => {
    const value = data?.items.map((item) => item?.items?.length!);
    const row = Math.max.apply(0, value!);
    const column = data?.items.length!;
    const category = data?.items.map((item) => item.items);
    const newArray: WOStatisticsItem[][] = Array.from(new Array(row), () =>
      new Array(column).fill({})
    );

    for (let i = 0; i < column; i++) {
      if (category[i].length! < row) {
        category[i].push(...new Array(row - category[i].length).fill(initializeStatisticsItem));
      }
      for (let j = 0; j < row; j++) {
        newArray[j][i] = category[i][j];
      }
    }
    setStatisticsItem(newArray);
  };

  useEffect(() => {
    categoryList?.items && itemsReverseHandler(categoryList);
  }, [categoryList]);

  return (
    <ListContainer>
      <SubTitle subTitle="통계관련 범례내용" />

      {isCategoryLoading ? (
        <MWProgress />
      ) : (
        <FormSection>
          <MWCaption
            title={"카테고리 추가"}
            icon={<AddTaskIcon />}
            link={"/Statistics/CreateCategory"}
          />
          <FormContents container rowSpacing={2}>
            <TableContainer
              component={Paper}
              // sx={{backgroundColor: "#fff", border: "1px solid grey"}}
            >
              <Table>
                <StyledTableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    {categoryList?.items.map((item) => (
                      <TableCell key={item.uid}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <p>{item.categoryName}</p>
                          <Box>
                            <Button
                              variant="outlined"
                              size="small"
                              style={{marginRight: "4px", minWidth: 0}}
                              onClick={() => {
                                navigate(`/Statistics/EditCategory/${item.uid}`, {
                                  state: item.uid,
                                });
                              }}
                            >
                              편집
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              style={{minWidth: 0}}
                              color="secondary"
                              onClick={() => {
                                setAlertState({
                                  toggle: true,
                                  message: "등록된 카테고리를 삭제하시겠습니까?",
                                  execution: () =>
                                    categoryRemoveMutate({token: tokenState, id: item.uid}),
                                });
                              }}
                            >
                              삭제
                            </Button>
                          </Box>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </StyledTableHead>
                <TableBody>
                  {statisticsItem?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{index + 1}</TableCell>
                      {item.map((value, idx) => (
                        <TableCell key={idx}>
                          <Box
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <p>{value.itemName}</p>
                            {value.itemName && (
                              <Box>
                                <IconButton
                                  color="success"
                                  size="small"
                                  onClick={() => {
                                    navigate(`/Statistics/EditItem1/${value.uid}`, {
                                      state: value.categoryUID,
                                    });
                                  }}
                                >
                                  <ModeEditIcon />
                                </IconButton>
                                <span>|</span>
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => {
                                    setAlertState({
                                      toggle: true,
                                      message: "설비이력카드를 삭제하시겠습니까?",
                                      execution: () =>
                                        Item1RemoveMutate({token: tokenState, id: value.uid}),
                                    });
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell></TableCell>
                    {categoryList?.items.map((item) => (
                      <TableCell key={item.uid} align="center">
                        <Button variant="contained" color="primary" size="small">
                          <Link to={`/Statistics/CreateItem1/${item.uid}`} style={{color: "#fff"}}>
                            추가
                          </Link>
                        </Button>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </FormContents>
        </FormSection>
      )}
      {isStatistics2Loading ? (
        <MWProgress />
      ) : (
        <FormSection>
          <MWCaption
            title={"상태별 내용 추가"}
            icon={<AddTaskIcon />}
            link={"/Statistics/CreateItem2"}
          />
          <FormContents container rowSpacing={2}>
            <TableContainer
              component={Paper}
              // sx={{backgroundColor: "#fff", border: "1px solid grey"}}
            >
              <Table>
                <StyledTableHead>
                  <TableRow>
                    <TableCell width="6%">#</TableCell>
                    <TableCell>결함내용</TableCell>
                    <TableCell align="center" width="20%">
                      편집
                    </TableCell>
                  </TableRow>
                </StyledTableHead>
                <TableBody>
                  {statistics2List?.items.map((item) => (
                    <TableRow key={item.uid}>
                      <TableCell>{item.uid}</TableCell>
                      <TableCell>{item.itemName}</TableCell>
                      <TableCell>
                        <Box sx={{display: "flex", justifyContent: "center"}}>
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            style={{marginRight: "12px"}}
                            onClick={() => {
                              navigate(`/Statistics/EditItem2/${item.uid}`);
                            }}
                          >
                            편집
                          </Button>
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            style={{marginRight: "12px"}}
                            onClick={() => {
                              setAlertState({
                                toggle: true,
                                message: "결함내용을 삭제하시겠습니까?",
                                execution: () =>
                                  Item2RemoveMutate({token: tokenState, id: item.uid}),
                              });
                            }}
                          >
                            삭제
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </FormContents>
        </FormSection>
      )}
    </ListContainer>
  );
};

export default StatisticsList;
